import React, { useEffect, useState } from 'react';
import { Modal, Box, Typography, Button, Fade, Backdrop } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';
import { fetchOffersFor } from '../../api/offers/offersApi';
import { rememberOffer } from '../../utils/offerSession';
import { copyText, ctaLabel, endsLabel, exploreAllPath, offerPath, offerStyle } from './offerFormat';
import { track } from '../../utils/tracker';

const NAVY = '#0D1F3C';
const GOLD = '#FFBD59';
const GOLD_DK = '#C69A4A';
const FONT = '"DM Sans", system-ui, sans-serif';
// Closing hides that offer for a few days; a new offer shows again straight away.
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;

const seenKey = (offer) => `offerSeen:${offer.id}`;
function wasDismissed(offer) {
  try {
    const at = Number(localStorage.getItem(seenKey(offer)) || 0);
    return at && Date.now() - at < SNOOZE_MS;
  } catch {
    return false;
  }
}
function markDismissed(offer) {
  try {
    localStorage.setItem(seenKey(offer), String(Date.now()));
  } catch {
    // ignore
  }
}

/**
 * The homepage popup. Content comes from the coupon an admin put on "Homepage popup"
 * (Coupons → Where to show) — no codes or seasons live in this file any more.
 */
export default function OfferPopup() {
  const navigate = useNavigate();
  const [offer, setOffer] = useState(null);
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showTerms, setShowTerms] = useState(false);

  useEffect(() => {
    let alive = true;
    let timer;
    fetchOffersFor('popup', 1).then(([first]) => {
      if (!alive || !first || wasDismissed(first)) return;
      setOffer(first);
      timer = setTimeout(() => {
        setOpen(true);
        track('offer_popup_view', { code: first.code });
      }, 900);
    });
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, []);

  if (!offer) return null;
  const style = offerStyle(offer);

  const close = () => {
    setOpen(false);
    markDismissed(offer);
  };
  const go = (path, action) => {
    rememberOffer(offer.code);
    track('offer_popup_click', { code: offer.code, action });
    setOpen(false);
    markDismissed(offer);
    navigate(path);
  };
  const copy = async (e) => {
    e.stopPropagation();
    if (await copyText(offer.code)) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    }
  };

  const chips = [
    `${offer.propertyCount} ${offer.propertyCount === 1 ? 'stay' : 'stays'}`,
    offer.cities?.length ? offer.cities.slice(0, 3).join(' · ') : null,
    Number(offer.type) === 1 && Number(offer.upto) > 0 ? `Max ₹${Number(offer.upto).toLocaleString('en-IN')} off` : null,
  ].filter(Boolean);

  return (
    <Modal open={open} onClose={close} closeAfterTransition slots={{ backdrop: Backdrop }}
      slotProps={{ backdrop: { timeout: 400, sx: { backgroundColor: 'rgba(9, 20, 40, 0.5)', zIndex: 2100 } } }}
      sx={{ zIndex: 2200 }}>
      <Fade in={open}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: { xs: '92%', sm: 440 }, maxHeight: '92vh', overflowY: 'auto', bgcolor: '#fff', borderRadius: '22px',
          boxShadow: '0 28px 70px rgba(13, 31, 60, 0.28)', outline: 'none', border: '1px solid #e2e6ee', zIndex: 2201,
        }}>
          <Box sx={{ position: 'relative', minHeight: 96, background: style.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 6, py: 2.5, overflow: 'hidden' }}>
            {offer.theme?.popupImageUrl && (
              <Box component="img" src={offer.theme.popupImageUrl} alt="" sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.35 }} />
            )}
            <Typography sx={{ position: 'relative', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: style.ink, fontFamily: FONT, textAlign: 'center' }}>
              {offer.theme?.eyebrow || 'Special offer'}{offer.endDate ? ` · ${endsLabel(offer.endDate)}` : ''}
            </Typography>
            <Box component="button" type="button" onClick={close} aria-label="Close"
              sx={{ position: 'absolute', top: 12, right: 12, width: 32, height: 32, borderRadius: '50%', border: 'none', bgcolor: 'rgba(255,255,255,0.16)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', '&:hover': { bgcolor: 'rgba(255,255,255,0.26)' } }}>
              <CloseIcon sx={{ fontSize: '1.1rem' }} />
            </Box>
          </Box>

          <Box sx={{ px: { xs: 3, sm: 4 }, pt: 2.6, pb: 3, textAlign: 'center', background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(255,189,89,0.16), transparent 55%), linear-gradient(180deg, #fffaf2 0%, #ffffff 100%)' }}>
            <Typography sx={{ fontFamily: '"Playfair Display", Georgia, serif', fontWeight: 600, fontSize: { xs: '1.5rem', sm: '1.7rem' }, color: NAVY, letterSpacing: '-0.02em', lineHeight: 1.25 }}>
              {offer.title || offer.discountLabel}
            </Typography>
            {offer.description && (
              <Typography sx={{ mt: 1, color: '#6b7280', fontSize: '0.9rem', lineHeight: 1.5, fontFamily: FONT }}>{offer.description}</Typography>
            )}
            <Box sx={{ mt: 1.4, display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.75 }}>
              {chips.map((c) => (
                <Box key={c} component="span" sx={{ px: 1.2, py: 0.35, borderRadius: 999, bgcolor: '#eef1f7', color: NAVY, fontSize: '0.72rem', fontWeight: 700, fontFamily: FONT }}>{c}</Box>
              ))}
            </Box>

            <Box component="button" type="button" onClick={copy}
              sx={{ mt: 2, width: '100%', display: 'flex', alignItems: 'stretch', border: `1.5px dashed ${GOLD_DK}`, borderRadius: '14px', overflow: 'hidden', bgcolor: '#fff8ec', cursor: 'pointer', p: 0, fontFamily: 'inherit' }}>
              <Box sx={{ flex: 1, py: 1.2, px: 1.5, textAlign: 'left' }}>
                <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: GOLD_DK, fontFamily: FONT }}>Your coupon</Typography>
                <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, letterSpacing: '0.12em', color: NAVY, fontFamily: FONT }}>{offer.code}</Typography>
              </Box>
              <Box sx={{ width: 92, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: GOLD, color: NAVY, fontSize: '0.78rem', fontWeight: 800, fontFamily: FONT }}>
                {copied ? 'Copied' : 'Copy'}
              </Box>
            </Box>

            <Box component="ul" sx={{ mt: 1.5, mb: 0, p: '8px 12px', listStyle: 'none', textAlign: 'left', bgcolor: '#f7f8fb', border: '1px solid #e6e9f0', borderRadius: '10px', fontSize: '0.8rem', color: '#4b5568', fontFamily: FONT }}>
              {(showTerms ? offer.terms : offer.rules.slice(0, 3)).map((line) => (
                <Box component="li" key={line} sx={{ display: 'flex', gap: 0.8, py: 0.25 }}>
                  <Box component="span" sx={{ color: '#1f7a4d', fontWeight: 800 }}>✓</Box>{line}
                </Box>
              ))}
            </Box>

            <Button variant="contained" fullWidth onClick={() => go(offerPath(offer), 'offer_stays')}
              sx={{ mt: 2, background: NAVY, color: '#fff', borderRadius: '12px', py: 1.2, fontWeight: 700, fontSize: '0.95rem', textTransform: 'none', fontFamily: FONT, boxShadow: '0 10px 24px rgba(13, 31, 60, 0.22)', '&:hover': { background: '#162c52' } }}>
              {ctaLabel(offer)} →
            </Button>
            <Button variant="outlined" fullWidth onClick={() => go(exploreAllPath(offer), 'explore_all')}
              sx={{ mt: 1.2, borderColor: NAVY, borderWidth: 1.5, color: NAVY, borderRadius: '12px', py: 1, fontWeight: 700, fontSize: '0.9rem', textTransform: 'none', fontFamily: FONT, '&:hover': { borderWidth: 1.5, borderColor: NAVY, bgcolor: '#f5f7fb' } }}>
              Explore all stays
            </Button>
            <Box sx={{ mt: 1.4, display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Typography component="button" type="button" onClick={() => setShowTerms((v) => !v)}
                sx={{ border: 'none', bgcolor: 'transparent', color: '#2457c5', fontSize: '0.8rem', cursor: 'pointer', fontFamily: FONT, p: 0 }}>
                {showTerms ? 'Hide T&C' : 'Full T&C'}
              </Typography>
              <Typography component="button" type="button" onClick={close}
                sx={{ border: 'none', bgcolor: 'transparent', color: '#9ca3af', fontSize: '0.8rem', cursor: 'pointer', fontFamily: FONT, p: 0, '&:hover': { color: '#6b7280' } }}>
                Maybe later
              </Typography>
            </Box>
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
}
